import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaLeftLong } from "react-icons/fa6";
import { toast } from "react-toastify"; // Added toast
import "react-toastify/dist/ReactToastify.css"; // Ensure toastify CSS is imported
import { loadRazorpay } from "../utils/loadRazorpay"; // CORRECTED import path

const DealDetails = () => {
  const { deal_uuid } = useParams();
  const [dealDetails, setDealDetails] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false); // New state for payment processing
  const navigate = useNavigate();

  // Fetches deal details based on the deal_uuid from URL params
  useEffect(() => {
    if (!deal_uuid) return; // Exit if deal_uuid is not available

    axios
      .get(`https://api.upswap.app/api/deals/details/${deal_uuid}`)
      .then((response) => {
        setDealDetails(response.data); // Set the fetched deal details
      })
      .catch((error) => {
        console.error("Error fetching deal details:", error);
        toast.error("Failed to load deal details. Please try again."); // Show error toast
      });
  }, [deal_uuid]); // Re-run effect if deal_uuid changes

  // Handles clicking on an image to open the fullscreen modal
  const handleImageClick = (imgUrl) => {
    setSelectedImage(imgUrl); // Set the image to display in modal
    setIsImageModalOpen(true); // Open the modal
  };

  // handleBuyNow function: Contains the Razorpay payment initiation logic
  const handleBuyNow = useCallback(
    async () => {
      // ✅ 1. Check if user is logged in (access_token in localStorage)
      const accessToken = localStorage.getItem("access");
      if (!accessToken) {
        toast.info("⚠️ Please login to purchase the deal.");
        return;
      }

      // ✅ 2. Check if deal details are loaded
      if (!dealDetails) {
        toast.error("Deal details are not loaded yet. Please wait.");
        return;
      }

      setIsProcessingPayment(true);
      const paymentInitToastId = toast.info("Initializing payment...", {
        autoClose: false,
        toastId: "paymentInit",
      });

      try {
        // Attempt to load the Razorpay SDK
        const res = await loadRazorpay();

        if (!res) {
          // If SDK failed to load
          toast.dismiss(paymentInitToastId); // Dismiss the initializing toast
          toast.warn(
            "Razorpay SDK failed to load. Please check your internet connection."
          );
          // Don't return here, let finally block handle state reset
          return;
        }

        // Check for user's phone number from local storage
        // const storedPhoneNumber = localStorage.getItem("phone_number");
        // if (!storedPhoneNumber) {
        //   // If phone number is missing
        //   toast.dismiss(paymentInitToastId);
        //   toast.error(
        //     "Phone number not found. Please update your profile to proceed with payment."
        //   );
        //   navigate("/UserProfile"); // Navigate to user profile to update phone number
        //   // Don't return here, let finally block handle state reset
        //   return;
        // }

        // Calculate the total amount based on the deal price and selected quantity
        const totalAmount = dealDetails.deal_price * quantity;

        // Razorpay payment options
        const options = {
          key: "rzp_live_vChMTg5Zq6WCJI", // Your Razorpay Live Key (as provided in the original code)
          amount: totalAmount * 100, // Amount in paisa (Razorpay expects amount in smallest currency unit)
          currency: "INR",
          name: "Upswap Deals",
          description: dealDetails.deal_title, // Deal title as payment description
          image: "/logo.png", // Path to your logo (ensure it's accessible)
          handler: function (response) {
            // This function is called on successful payment
            toast.dismiss(paymentInitToastId); // Dismiss the initial toast
            toast.success("✅ Payment Successful!"); // Show success toast
            console.log("Payment ID:", response.razorpay_payment_id); // Log payment ID
            // isProcessingPayment will be reset in finally block
          },
          prefill: {
            name: "Customer Name", // Placeholder, ideally fetch from user's actual profile
            email: "user@example.com", // Placeholder, ideally fetch from user's actual profile
            // contact: storedPhoneNumber, // Prefill with stored phone number
          },
          theme: {
            color: "#FE7A3A", // Theme color for Razorpay modal
          },
        };

        // Create a new Razorpay instance
        const rzp = new window.Razorpay(options);

        // Event handler for failed payments
        rzp.on("payment.failed", function (response) {
          toast.dismiss(paymentInitToastId);
          toast.error(
            `❌ Payment Failed: ${
              response.error.description || "Something went wrong."
            }`
          );
          console.error("Razorpay Payment Failed:", response.error);
          // isProcessingPayment will be reset in finally block
        });

        // Event handler for when the payment modal is closed by the user
        rzp.on("payment.modal.closed", function () {
          toast.dismiss(paymentInitToastId);
          toast.info("Payment process cancelled.");
          // isProcessingPayment will be reset in finally block
        });

        rzp.open(); // Open the Razorpay payment modal
        toast.dismiss(paymentInitToastId); // Dismiss the info toast once modal is open
      } catch (err) {
        // Catch any errors during the try block (e.g., network issues before Razorpay opens)
        toast.dismiss(paymentInitToastId);
        console.error("Error initiating Razorpay:", err);
        toast.error("Failed to initiate payment. Please try again.");
      } finally {
        // This block will always execute after try/catch, ensuring processing state is reset
        setIsProcessingPayment(false);
      }
    },
    // Dependencies for useCallback: re-create function if these values change
    [dealDetails, quantity, navigate]
  );

  // Display loading spinner if deal details are not yet loaded
  if (!dealDetails) {
    return (
      <div className="flex justify-center items-center h-screen w-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="py-8 px-4 sm:px-6 md:px-8 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r bg-[#FE7A3A] text-white py-3 px-4 rounded-lg flex items-center justify-between">
        <button
          onClick={() => navigate("/PopularDeals")} // Navigate back to ExclusiveDeals
          className="text-white text-sm px-1 py-1 rounded-md hover:bg-red-500"
        >
          <FaLeftLong className="w-5 h-5" />
        </button>
        <h2 className="text-lg font-semibold text-center flex-1">
          Deal Description
        </h2>
        <div className="w-14" /> {/* Placeholder to keep title centered */}
      </div>

      {/* Title and vendor */}
      <div className="p-4">
        <h1 className="text-xl font-bold">{dealDetails.deal_title}</h1>
        <p className="text-sm text-gray-600">
          Posted by:{" "}
          <span className="text-orange-500 font-medium">
            {dealDetails.vendor_name}
          </span>
        </p>
        <p className="text-sm text-red-500 mt-1">
          📍 {dealDetails.location_city}, {dealDetails.location_state}
        </p>
      </div>

      {/* Images preview */}
      <div className="relative mx-4 flex gap-2 flex-wrap justify-center">
        {dealDetails.uploaded_images?.map((img, index) => (
          <img
            key={index}
            src={img}
            alt={`Detail of deal ${index + 1}`}
            onClick={() => handleImageClick(img)}
            className="w-52 h-52 object-cover rounded-md cursor-pointer border-2 border-gray-200 hover:scale-105 transition-all duration-200"
          />
        ))}

        <div className="absolute top-2 left-2 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">
          {dealDetails.available_deals} DEALS REMAINING
        </div>
      </div>

      {/* Description */}
      <div className="p-4">
        <h3 className="text-lg font-semibold mb-1 mt-4">Deal Description</h3>
        <p className="text-sm text-gray-700 whitespace-pre-line">
          {dealDetails.deal_description}
        </p>
      </div>

      {/* Price and quantity input */}
      <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200">
        <div>
          <p className="text-sm line-through text-gray-400">
            ₹ {dealDetails.actual_price}
          </p>
          <p className="text-xl font-bold text-black">
            ₹ {dealDetails.deal_price}{" "}
            <span className="text-sm text-green-600">
              -{dealDetails.discount_percentage}%
            </span>
          </p>
        </div>

        {/* Quantity Editable Input */}
        <div className="flex items-center border border-gray-300 rounded-full px-2 py-1 bg-white shadow-sm">
          <button
            onClick={() => setQuantity((q) => (q > 1 ? q - 1 : 1))}
            className="text-orange-500 text-xl font-bold px-3"
          >
            –
          </button>

          <input
            type="text"
            value={quantity}
            onChange={(e) => {
              const val = parseInt(e.target.value);
              // Ensure quantity is a number and at least 1
              if (!isNaN(val) && val >= 1) setQuantity(val);
            }}
            className="w-10 text-center outline-none border-none bg-transparent text-base font-medium"
          />

          <button
            onClick={() => setQuantity((q) => q + 1)}
            className="text-orange-500 text-xl font-bold px-3"
          >
            +
          </button>
        </div>
      </div>

      {/* Buy Now button */}
      <div className="p-4">
        <button
          onClick={handleBuyNow} // Added onClick handler to trigger payment
          disabled={isProcessingPayment} // Button is disabled when payment is processing
          className={`w-full font-semibold py-3 rounded-md transition ${
            isProcessingPayment
              ? "bg-gray-400 cursor-not-allowed" // Greyed out when disabled
              : "bg-orange-500 hover:bg-orange-600 text-white" // Normal style
          }`}
        >
          {isProcessingPayment ? "Processing..." : "Buy Now"}{" "}
          {/* Text changes when processing */}
        </button>
      </div>

      {/* Fullscreen image preview modal */}
      {isImageModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 rounded-lg"
          onClick={() => setIsImageModalOpen(false)} // Close modal on overlay click
        >
          <img
            src={selectedImage}
            alt="Preview"
            className="w-1/2 h-1/2 object-contain rounded-lg shadow-lg"
          />
        </div>
      )}
    </div>
  );
};

export default DealDetails;

// import React, { useState, useEffect } from "react";
// import { useParams } from "react-router-dom";
// import axios from "axios";
// import { useNavigate } from "react-router-dom";
// import { FaLeftLong } from "react-icons/fa6";
// import { LuLoader } from "react-icons/lu";
// import PopularDeals from "../components/Deals/PopularDeals";

// const DealDetails = () => {
//   const { deal_uuid } = useParams();
//   const [dealDetails, setDealDetails] = useState(null);
//   const [quantity, setQuantity] = useState(1);
//   const [selectedImage, setSelectedImage] = useState(null);
//   const [isImageModalOpen, setIsImageModalOpen] = useState(false);
//   const navigate = useNavigate();

//   useEffect(() => {
//     if (!deal_uuid) return;

//     axios
//       .get(`https://api.upswap.app/api/deals/details/${deal_uuid}`)
//       .then((response) => {
//         setDealDetails(response.data);
//       })
//       .catch((error) => {
//         console.error("Error fetching deal details:", error);
//       });
//   }, [deal_uuid]);

//   const handleImageClick = (imgUrl) => {
//     setSelectedImage(imgUrl);
//     setIsImageModalOpen(true);
//   };

//   if (!dealDetails) {
//     return (
//       <div className="flex justify-center items-center h-screen w-screen">
//         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
//       </div>
//     );
//   }

//   return (
//     // <div className="p-4 bg-white min-h-screen">
//     <div className="py-8 px-4 sm:px-6 md:px-8 max-w-screen-2xl mx-auto">
//       {/* Header */}
//       <div className="bg-gradient-to-r bg-[#FE7A3A] text-white py-3 px-4 rounded-lg flex items-center justify-between">
//         <button
//           onClick={() => navigate("/PopularDeals")}
//           className="text-white text-sm px-1 py-1 rounded-md hover:bg-red-500"
//         >
//           <FaLeftLong className="w-5 h-5" />
//         </button>
//         <h2 className="text-lg font-semibold text-center flex-1">
//           Deal Description
//         </h2>
//         <div className="w-14" /> {/* Placeholder to keep title centered */}
//       </div>

//       {/* Title and vendor */}
//       <div className="p-4">
//         <h1 className="text-xl font-bold">{dealDetails.deal_title}</h1>
//         <p className="text-sm text-gray-600">
//           Posted by:{" "}
//           <span className="text-orange-500 font-medium">
//             {dealDetails.vendor_name}
//           </span>
//         </p>
//         <p className="text-sm text-red-500 mt-1">
//           📍 {dealDetails.location_city}, {dealDetails.location_state}
//         </p>
//       </div>

//       {/* Images preview */}
//       <div className="relative mx-4 flex gap-2 flex-wrap justify-center">
//         {dealDetails.uploaded_images?.map((img, index) => (
//           <img
//             key={index}
//             src={img}
//             // src={img || img || "/default-avatar.png"}
//             alt={`Detail of deal ${index + 1}`} // Changed this line
//             onClick={() => handleImageClick(img)}
//             className="w-52 h-52 object-cover rounded-md cursor-pointer border-2 border-gray-200 hover:scale-105 transition-all duration-200"
//           />
//         ))}

//         <div className="absolute top-2 left-2 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">
//           {dealDetails.available_deals} DEALS REMAINING
//         </div>
//       </div>

//       {/* Description */}
//       <div className="p-4">
//         <h3 className="text-lg font-semibold mb-1 mt-4">Deal Description</h3>
//         <p className="text-sm text-gray-700 whitespace-pre-line">
//           {dealDetails.deal_description}
//         </p>
//       </div>

//       {/* Price and quantity input */}
//       <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200">
//         <div>
//           <p className="text-sm line-through text-gray-400">
//             ₹ {dealDetails.actual_price}
//           </p>
//           <p className="text-xl font-bold text-black">
//             ₹ {dealDetails.deal_price}{" "}
//             <span className="text-sm text-green-600">
//               -{dealDetails.discount_percentage}%
//             </span>
//           </p>
//         </div>

//         {/* Quantity Editable Input */}
//         <div className="flex items-center border border-gray-300 rounded-full px-2 py-1 bg-white shadow-sm">
//           <button
//             onClick={() => setQuantity((q) => (q > 1 ? q - 1 : 1))}
//             className="text-orange-500 text-xl font-bold px-3"
//           >
//             –
//           </button>

//           <input
//             type="text"
//             value={quantity}
//             onChange={(e) => {
//               const val = parseInt(e.target.value);
//               if (!isNaN(val) && val >= 1) setQuantity(val);
//             }}
//             className="w-10 text-center outline-none border-none bg-transparent text-base font-medium"
//           />

//           <button
//             onClick={() => setQuantity((q) => q + 1)}
//             className="text-orange-500 text-xl font-bold px-3"
//           >
//             +
//           </button>
//         </div>
//       </div>

//       {/* Buy Now button */}
//       <div className="p-4">
//         <button className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 rounded-md">
//           Buy now
//         </button>
//       </div>

//       {/* Fullscreen image preview modal */}
//       {isImageModalOpen && (
//         <div
//           className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 rounded-lg"
//           onClick={() => setIsImageModalOpen(false)}
//         >
//           <img
//             src={selectedImage}
//             alt="Preview"
//             className="w-1/2 h-1/2 object-contain rounded-lg shadow-lg"
//           />
//         </div>
//       )}
//     </div>
//   );
// };

// export default DealDetails;

// src/pages/DealDetails.jsx
// src/pages/DealDetails.jsx

// import React, { useState, useEffect } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import axios from "axios";
// import { FaLeftLong } from "react-icons/fa6";
// import { useAuth } from "./Authentication/AuthContext"; // Import useAuth hook
// import { toast, ToastContainer } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";

// const DealDetails = () => {
//   const { deal_uuid } = useParams();
//   const [dealDetails, setDealDetails] = useState(null);
//   const [quantity, setQuantity] = useState(1);
//   const [selectedImage, setSelectedImage] = useState(null);
//   const [isImageModalOpen, setIsImageModalOpen] = useState(false);
//   const navigate = useNavigate();
//   const { isAuthenticated, userRole, isPublic, isRegularUser } = useAuth(); // Destructure from useAuth

//   useEffect(() => {
//     if (!deal_uuid) return;

//     axios
//       .get(`https://api.upswap.app/api/deals/details/${deal_uuid}`)
//       .then((response) => {
//         setDealDetails(response.data);
//       })
//       .catch((error) => {
//         console.error("Error fetching deal details:", error);
//         toast.error("Error fetching deal details.");
//       });
//   }, [deal_uuid]);

//   const handleImageClick = (imgUrl) => {
//     setSelectedImage(imgUrl);
//     setIsImageModalOpen(true);
//   };

//   const handleBuyNow = () => {
//     if (!isAuthenticated) {
//       toast.info("Please login to buy this deal.");
//       navigate("/login"); // Redirect to login page
//       return;
//     }
//     if (userRole === "vendor") {
//       toast.warn("Vendors cannot buy deals. Switch to a user account.");
//       return;
//     }
//     // Proceed with buying logic if user is authenticated and is a regular user
//     toast.success(
//       `Buying ${quantity} of ${dealDetails.deal_title}! (Dummy action)`
//     );
//     // Here, you would integrate your actual API call for purchasing the deal
//     // Example: axios.post('/api/purchase-deal', { deal_uuid, quantity, ... })
//   };

//   if (!dealDetails) {
//     return (
//       <div className="flex justify-center items-center h-screen w-screen">
//         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
//       </div>
//     );
//   }

//   return (
//     <div className="py-8 px-4 sm:px-6 md:px-8 max-w-screen-2xl mx-auto">
//       <ToastContainer position="top-center" autoClose={3000} />
//       {/* Header */}
//       <div className="bg-gradient-to-r bg-[#FE7A3A] text-white py-3 px-4 rounded-lg flex items-center justify-between">
//         {/* // Option 1: Move comment inside the button element */}
//         <button
//           onClick={() => navigate(-1)}
//           className="text-white text-sm px-1 py-1 rounded-md hover:bg-red-500"
//         >
//           {/* Go back to previous page */}
//           <FaLeftLong className="w-5 h-5" />
//         </button>
//         <h2 className="text-lg font-semibold text-center flex-1">
//           Deal Description
//         </h2>
//         <div className="w-14" /> {/* Placeholder to keep title centered */}
//       </div>

//       {/* Title and vendor */}
//       <div className="p-4">
//         <h1 className="text-xl font-bold">{dealDetails.deal_title}</h1>
//         <p className="text-sm text-gray-600">
//           Posted by:{" "}
//           <span
//             className="text-orange-500 font-medium cursor-pointer hover:underline"
//             onClick={() => navigate(`/vendor-profile/${dealDetails.vendor_id}`)} // Navigate to vendor profile
//           >
//             {dealDetails.vendor_name}
//           </span>
//         </p>
//         <p className="text-sm text-red-500 mt-1">
//           📍 {dealDetails.location_city}, {dealDetails.location_state}
//         </p>
//       </div>

//       {/* Images preview */}
//       <div className="relative mx-4 flex gap-2 flex-wrap justify-center">
//         {dealDetails.uploaded_images?.map((img, index) => (
//           <img
//             key={index}
//             src={img}
//             alt={`Detail of deal ${index + 1}`}
//             onClick={() => handleImageClick(img)}
//             className="w-52 h-52 object-cover rounded-md cursor-pointer border-2 border-gray-200 hover:scale-105 transition-all duration-200"
//           />
//         ))}

//         <div className="absolute top-2 left-2 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">
//           {dealDetails.available_deals} DEALS REMAINING
//         </div>
//       </div>

//       {/* Description */}
//       <div className="p-4">
//         <h3 className="text-lg font-semibold mb-1 mt-4">Deal Description</h3>
//         <p className="text-sm text-gray-700 whitespace-pre-line">
//           {dealDetails.deal_description}
//         </p>
//       </div>

//       {/* Price and quantity input */}
//       <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200">
//         <div>
//           <p className="text-sm line-through text-gray-400">
//             ₹ {dealDetails.actual_price}
//           </p>
//           <p className="text-xl font-bold text-black">
//             ₹ {dealDetails.deal_price}{" "}
//             <span className="text-sm text-green-600">
//               -{dealDetails.discount_percentage}%
//             </span>
//           </p>
//         </div>

//         {/* Quantity Editable Input - Only for authenticated users (regular or vendor, though vendors can't buy) */}
//         {(isRegularUser || userRole === "vendor") && ( // Show quantity for authenticated users
//           <div className="flex items-center border border-gray-300 rounded-full px-2 py-1 bg-white shadow-sm">
//             <button
//               onClick={() => setQuantity((q) => (q > 1 ? q - 1 : 1))}
//               className="text-orange-500 text-xl font-bold px-3"
//             >
//               –
//             </button>
//             <input
//               type="text"
//               value={quantity}
//               onChange={(e) => {
//                 const val = parseInt(e.target.value);
//                 if (!isNaN(val) && val >= 1) setQuantity(val);
//               }}
//               className="w-10 text-center outline-none border-none bg-transparent text-base font-medium"
//             />
//             <button
//               onClick={() => setQuantity((q) => q + 1)}
//               className="text-orange-500 text-xl font-bold px-3"
//             >
//               +
//             </button>
//           </div>
//         )}
//       </div>

//       {/* Conditional Buy Now button */}
//       <div className="p-4">
//         {isPublic ? (
//           <button
//             onClick={handleBuyNow}
//             className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 rounded-md"
//           >
//             Login to Buy
//           </button>
//         ) : isRegularUser ? (
//           <button
//             onClick={handleBuyNow}
//             className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 rounded-md"
//           >
//             Buy now
//           </button>
//         ) : (
//           // userRole === 'vendor'
//           <button
//             className="w-full bg-gray-400 text-white font-semibold py-3 rounded-md cursor-not-allowed"
//             disabled
//           >
//             Vendors cannot buy deals
//           </button>
//         )}
//       </div>

//       {/* Fullscreen image preview modal */}
//       {isImageModalOpen && (
//         <div
//           className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 rounded-lg"
//           onClick={() => setIsImageModalOpen(false)}
//         >
//           <img
//             src={selectedImage}
//             alt="Preview"
//             className="w-1/2 h-1/2 object-contain rounded-lg shadow-lg"
//           />
//         </div>
//       )}
//     </div>
//   );
// };

// export default DealDetails;
